import { useMemo, useState } from "react";
import { HelpLink } from "../app/HelpLink";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Plus, Trash2 } from "lucide-react";
import { DateNav } from "../app/DateNav";
import { Button, Input } from "../ui";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDoc, useDocSelector } from "../core/useDocSelector";
import { useNow } from "../core/useNow";
import { listTasks } from "../core/tasks";
import { fmtMinutes, fmtTime } from "../core/format";
import type { TimelogEntry } from "../core/computed";
import { addEntry, deleteEntry, entriesOf, minutesOnDay, shardIdFor, updateEntry } from "./timelog";
import { TaskPicker } from "../tasks/TaskPicker";
import { AsideReview } from "../tasks/AsideReview";
import { useSettings } from "../app/settings";
import { DayDone, WorklogTabs } from "./DayDone";

function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function withTime(dayIso: string, hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(dayIso + "T00:00:00");
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/**
 * The Worklog's *Day* (spec §9.6; UI cleanup U4): what got done and carry-over, the time entries of the day with
 * manual edit (a running entry shows live), and once a week the look at what is set aside.
 */
export function WorklogPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { date } = useParams();
  const now = useNow();
  const day = date ?? isoDay(new Date());
  const dayDate = new Date(day + "T12:00:00");
  const { handle: core, workspaceId } = useCoreDoc();
  const settings = useSettings(core);
  const shard = useDoc(workspaceId ? shardIdFor(workspaceId, dayDate) : null);
  const entries = useDocSelector(shard, (doc) => entriesOf(doc)) ?? [];
  const tasksSel = useDocSelector(core, (doc) => listTasks(doc));
  const tasks = useMemo(() => tasksSel ?? [], [tasksSel]);
  const byId = useMemo(() => new Map(tasks.map((x) => [x.id, x])), [tasks]);
  const [adding, setAdding] = useState<string | null>(null);

  const dayStart = new Date(day + "T00:00:00").getTime();
  const nextDay = new Date(dayStart);
  nextDay.setDate(nextDay.getDate() + 1); // 23 or 25 hours on the days the clocks change (bug hunt M12)
  const dayEnd = nextDay.getTime();
  const todays = entries
    .filter((e) => {
      const s = Date.parse(e.start);
      const f = e.end ? Date.parse(e.end) : now;
      return s < dayEnd && f >= dayStart;
    })
    .sort((a, b) => a.start.localeCompare(b.start));
  const total = minutesOnDay(todays, dayDate, now);
  const shift = (days: number) => {
    const d = new Date(dayDate);
    d.setDate(d.getDate() + days);
    navigate(`/worklog/${isoDay(d)}`);
  };
  const ro = shard?.readOnly ?? true;

  const edit = (e: TimelogEntry, patch: Parameters<typeof updateEntry>[2]) =>
    shard && updateEntry(shard.doc, e.id, patch);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-5 mid:px-9 mid:pt-8">
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-[26px] font-semibold">{t("nav.worklog")}</h1>
        <HelpLink page="guides/worklog/" />
        <WorklogTabs />
        <div className="ml-auto">
          <DateNav
            label={dayDate.toLocaleDateString(i18n.language, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
            current={day === isoDay(new Date(now))}
            currentLabel={t("planner.today")}
            prevLabel={t("worklog.previousDay")}
            nextLabel={t("worklog.nextDay")}
            onPrev={() => shift(-1)}
            onNext={() => shift(1)}
            onCurrent={() => navigate(`/worklog/${isoDay(new Date(now))}`)}
            testId="worklog-nav"
          />
        </div>
      </div>
      <DayDone
        core={core}
        tasks={tasks}
        day={day}
        isToday={day === isoDay(new Date(now))}
        showOpenCount={settings.summary.showOpenCount}
        openTask={(id) => navigate(`/t/${id}`)}
      />
      <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {t("day.time")}
      </h2>
      <p className="mb-3 text-sm text-neutral-500" data-testid="worklog-total">
        {t("worklog.total", { d: fmtMinutes(total) })}
      </p>
      {todays.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-12 text-center text-neutral-500 dark:border-neutral-700">
          {t("worklog.empty")}
        </div>
      ) : (
        <ul
          className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800"
          data-testid="worklog-list"
        >
          {todays.map((e) => {
            const running = e.end === null;
            const mins = ((e.end ? Date.parse(e.end) : now) - Date.parse(e.start)) / 60_000;
            return (
              <li key={e.id} className="flex flex-col gap-1.5 px-4 py-2.5" data-testid="worklog-row">
                {/* line 1: task, from – to, duration (the times wrap under the task in a narrow column) */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <button
                    type="button"
                    className="min-w-0 flex-1 basis-40 truncate text-left text-sm hover:text-accent"
                    onClick={() => navigate(`/t/${e.taskId}`)}
                  >
                    {byId.get(e.taskId)?.title ?? t("task.untitled")}
                  </button>
                  {!running && e.start < new Date(dayStart).toISOString() && (
                    <span className="text-[12px] text-neutral-400">{fmtTime(e.start, i18n.language)}</span>
                  )}
                  <Input
                    compact
                    type="time"
                    aria-label={t("worklog.start")}
                    className="w-[6.5rem]"
                    value={toLocalInput(e.start)}
                    disabled={ro}
                    onChange={(ev) => ev.target.value && edit(e, { start: withTime(day, ev.target.value) })}
                  />
                  <span className="text-neutral-400">–</span>
                  {running ? (
                    <span className="w-[6.5rem] font-mono text-[13px] text-ok">{t("worklog.running")}</span>
                  ) : (
                    <Input
                      compact
                      type="time"
                      aria-label={t("worklog.end")}
                      className="w-[6.5rem]"
                      value={toLocalInput(e.end!)}
                      disabled={ro}
                      onChange={(ev) => ev.target.value && edit(e, { end: withTime(day, ev.target.value) })}
                    />
                  )}
                  <span className="ml-auto min-w-12 text-right font-mono text-[13px] tabular-nums text-neutral-500">
                    {fmtMinutes(mins)}
                  </span>
                </div>
                {/* line 2: the note, with delete at its end */}
                <div className="flex items-center gap-2">
                  <Input
                    compact
                    aria-label={t("worklog.note")}
                    placeholder={t("worklog.note")}
                    className="min-w-0 flex-1"
                    defaultValue={e.note ?? ""}
                    disabled={ro}
                    onBlur={(ev) => ev.target.value !== (e.note ?? "") && edit(e, { note: ev.target.value })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t("common.delete")}
                    title={t("common.delete")}
                    className="w-[30px] shrink-0 px-0"
                    disabled={ro}
                    onClick={() => shard && deleteEntry(shard.doc, e.id)}
                  >
                    <Trash2 size={14} aria-hidden />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {!ro && (
        <div className="mt-4 flex items-center gap-2">
          {adding === null ? (
            <Button variant="secondary" size="sm" onClick={() => setAdding("")}>
              <Plus size={14} aria-hidden /> {t("worklog.add")}
            </Button>
          ) : (
            <div className="w-80">
              <TaskPicker
                tasks={tasks}
                exclude={new Set()}
                placeholder={t("worklog.pickTask")}
                testId="worklog-task-picker"
                onPick={(taskId) => {
                  if (!shard) return;
                  const start = new Date(dayDate);
                  start.setHours(9, 0, 0, 0);
                  const end = new Date(start.getTime() + 30 * 60_000);
                  addEntry(shard.doc, { taskId, start: start.toISOString(), end: end.toISOString() });
                  setAdding(null);
                }}
              />
            </div>
          )}
        </div>
      )}
      {day === isoDay(new Date(now)) && core && !core.readOnly && (
        <AsideReview
          tasks={tasks}
          nowMs={now}
          write={(fn) => fn(core.doc)}
          openTask={(id) => navigate(`/t/${id}`)}
        />
      )}
    </div>
  );
}
